import { Input } from '@mantine/core'
import { mapPropertiesForPropertySelect } from 'App/irrigation-scheduler/utils/mapPropertiesForPropertySelect'
import { InfoMessageBox } from 'components/InfoMessageBox/InfoMessageBox'
import { PropertySelect } from 'components/PropertySelect/PropertySelect'
import { WeekRangePicker } from 'components/WeekRangePicker/WeekRangePicker'
import { getLastCompletedWeekEnd } from 'components/WeekRangePicker/weekRangeUtils'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { getPermittedIrrigationReportProperties } from './getPermittedIrrigationReportProperties'
import type { TEmailValidationError } from './validateEmails'
import { MAX_EMAIL_RECIPIENTS, validateEmails } from './validateEmails'

export const IrrigationReport = ({
  dates,
  emailErrors,
  emails,
  error,
  handleDateChange,
  loading,
  selectedPropertyId,
  setEmailErrors,
  setEmails,
  setSelectedPropertyId,
}: {
  dates: [Date | null, Date | null]
  emailErrors: TEmailValidationError | null
  emails: string
  error: string | null
  handleDateChange: (e: [Date | null, Date | null]) => void
  loading: boolean
  selectedPropertyId: number | null
  setEmailErrors: React.Dispatch<React.SetStateAction<TEmailValidationError | null>>
  setEmails: React.Dispatch<React.SetStateAction<string>>
  setSelectedPropertyId: React.Dispatch<React.SetStateAction<number | null>>
}) => {
  const fass = fieldAssetStore.useSelector((s) => s)
  const permittedProperties = getPermittedIrrigationReportProperties(fass)
  const browserTimezone = moment.tz.guess()

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 20, margin: 20 }}>
      <div>{translate.phrases.banyanApp('Property')}</div>
      <PropertySelect
        selectedPropertyId={selectedPropertyId}
        onChange={setSelectedPropertyId}
        disabled={isEmpty(permittedProperties)}
        data={mapPropertiesForPropertySelect(permittedProperties)}
        clearable={false}
      />
      {error && <div css={{ color: colors.red, textAlign: 'center' }}>{error}</div>}
      <WeekRangePicker
        value={dates}
        label={translate.phrases.banyanApp('Select Date Range')}
        placeholder={translate.phrases.banyanApp('Select Date Range')}
        disabled={loading}
        onChange={handleDateChange}
        maxDate={getLastCompletedWeekEnd(browserTimezone).toDate()}
      />
      <div>
        <div css={{ marginBottom: 4 }}>{translate.phrases.banyanApp('Email Address(es) (optional)')}</div>
        <div css={{ color: colors.grey800, fontSize: 14, marginBottom: 8 }}>
          {translate.phrases.banyanApp('You can enter multiple email addresses separated by commas.')}
        </div>
        <Input
          placeholder={translate.phrases.banyanApp('Enter email addresses separated by commas')}
          value={emails}
          onChange={(e) => {
            const value = e.currentTarget.value

            setEmails(value)

            setEmailErrors(validateEmails(value))
          }}
          css={{
            border: emailErrors ? `1px solid ${colors.red}` : 'none',
          }}
        />
        {emailErrors && (
          <div css={{ color: colors.red, fontSize: 14, marginTop: 6 }}>
            {emailErrors === 'TOO_MANY_EMAILS'
              ? translate.phrases.banyanApp('You can enter a maximum of {{max}} email addresses.', {
                  max: MAX_EMAIL_RECIPIENTS,
                })
              : translate.phrases.validation('{{label}} is invalid', {
                  label: translate.phrases.banyanApp('Email'),
                })}
          </div>
        )}
      </div>
      <InfoMessageBox>
        {emails.trim().length > 0
          ? translate.phrases.banyanApp(
              'Report may take up to 20 minutes to arrive in your inbox. Please check your spam folder.',
            )
          : translate.phrases.banyanApp(
              'Report may take up to 20 minutes to generate. It will be available to download from this page.',
            )}
      </InfoMessageBox>
    </div>
  )
}
